import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from "@angular/core";
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {Api, ApiService} from "../../../../../../common/services/api.service";
import {Api3Service} from "../../../../../../common/services/api3.service";
import {ToastService} from "../../../../../../common/services/toast.service";
import {User} from "../../../../../../common/interfaces/user.interface";
import {Warehouse} from "../../../../../../common/interfaces/warehouse.interface";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";

interface ILtlRule {
    _id?: string;
    id?: string;
    hubs: number[];
    packageTypes: number[];
    setOnHold: boolean;
    remark: string;
    order?: number;
}

@Component({
    selector: "partner-settings-ltl",
    templateUrl: "ltl-settings.component.html",
    styleUrls: ["ltl-settings.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PartnerSettingsLtlComponent implements OnInit, OnChanges, OnDestroy {

    @Input()
    public partner: User.IPartner;

    @Output()
    public updated: EventEmitter<void> = new EventEmitter<void>();

    public form: FormGroup = this.formBuilder.group({
        rules: this.formBuilder.array([])
    });

    public hubs: Warehouse.IHub[] = [];
    public packageTypes: Warehouse.IPackageType[] = [];

    public loading: { hubs: boolean; packageTypes: boolean } = {hubs: false, packageTypes: false};
    public saving: boolean = false;

    public hubSearchControls: { [key: number]: FormControl } = {};
    public filteredHubs: { [key: number]: Warehouse.IHub[] } = {};

    private readonly defaultRemark: string = "LTL shipment activated.";
    private destroy$: EventEmitter<boolean> = new EventEmitter<boolean>();

    public constructor(
        private formBuilder: FormBuilder,
        private api3Service: Api3Service,
        private apiService: ApiService,
        private toastService: ToastService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
    }

    public ngOnInit(): void {
        this.loadResources();
        this.loadLtlSettings();
        this.updateFormControlsDisabledState();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes["partner"] && !changes["partner"].firstChange) {
            this.loadLtlSettings();
        }
    }

    public get rules(): FormArray {
        return this.form.get("rules") as FormArray;
    }

    public trackRule(index: number, control: AbstractControl): AbstractControl {
        return control;
    }

    public addRule(rule?: ILtlRule): void {
        const index = this.rules.length;
        this.rules.push(this.createRuleGroup(rule));
        this.initHubSearchControl(index);
        this.form.markAsDirty();
        this.changeDetectorRef.markForCheck();
    }

    public removeRule(index: number): void {
        if (this.hubSearchControls[index]) {
            this.hubSearchControls[index].setValue("");
        }
        delete this.hubSearchControls[index];
        delete this.filteredHubs[index];

        this.rules.removeAt(index);

        const newControls: { [key: number]: FormControl } = {};
        const newFiltered: { [key: number]: Warehouse.IHub[] } = {};
        Object.keys(this.hubSearchControls).forEach(key => {
            const oldIndex = parseInt(key, 10);
            if (oldIndex > index) {
                newControls[oldIndex - 1] = this.hubSearchControls[oldIndex];
                newFiltered[oldIndex - 1] = this.filteredHubs[oldIndex];
            } else if (oldIndex < index) {
                newControls[oldIndex] = this.hubSearchControls[oldIndex];
                newFiltered[oldIndex] = this.filteredHubs[oldIndex];
            }
        });
        this.hubSearchControls = newControls;
        this.filteredHubs = newFiltered;
        this.form.markAsDirty();
        this.changeDetectorRef.markForCheck();
    }

    private initHubSearchControl(ruleIndex: number): void {
        const searchControl = new FormControl("");
        this.hubSearchControls[ruleIndex] = searchControl;
        this.filteredHubs[ruleIndex] = [...this.hubs];

        searchControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe((searchText: string) => {
                this.filterHubs(ruleIndex, searchText || "");
            });
    }

    private filterHubs(ruleIndex: number, searchText: string): void {
        const search = searchText.toLowerCase().trim();
        if (!search) {
            this.filteredHubs[ruleIndex] = [...this.hubs];
        } else {
            this.filteredHubs[ruleIndex] = this.hubs.filter(hub => {
                const name = (hub.customers_inventory_name || "").toLowerCase();
                const subInventory = ((hub.customers_sub_inventory || hub.sub_inventory) || "").toLowerCase();
                return name.includes(search) || subInventory.includes(search);
            });
        }
        this.changeDetectorRef.markForCheck();
    }

    public getFilteredHubs(ruleIndex: number): Warehouse.IHub[] {
        return this.filteredHubs[ruleIndex] || this.hubs;
    }

    public getHubSearchControl(ruleIndex: number): FormControl {
        if (!this.hubSearchControls[ruleIndex]) {
            this.initHubSearchControl(ruleIndex);
        }
        return this.hubSearchControls[ruleIndex];
    }

    public async save(): Promise<void> {
        if (!this.partner) {
            return;
        }

        if (this.hasIncompleteRules()) {
            this.toastService.show("Please select hubs and package types for every rule before saving", "error");
            return;
        }

        this.saving = true;
        this.updateFormControlsDisabledState();
        this.changeDetectorRef.markForCheck();

        try {
            const rulesToSave = this.rules.controls.map((group: FormGroup, index: number): ILtlRule => {
                const value = group.getRawValue();
                return {
                    _id: value._id || value.id,
                    id: value._id || value.id,
                    hubs: Array.isArray(value.hubs) ? [...value.hubs] : [],
                    packageTypes: Array.isArray(value.packageTypes) ? [...value.packageTypes] : [],
                    setOnHold: !!value.setOnHold,
                    remark: value.remark || "",
                    order: value.order !== undefined ? value.order : index
                };
            });

            const existingRules = await this.api3Service.get(`partner/${this.partner.slug}/ltl-rules`);
            const existingIds = (existingRules?.data || []).map((r: ILtlRule) => r._id || r.id).filter(Boolean);
            const newRuleIds = rulesToSave.map(r => r._id || r.id).filter(Boolean);
            const idsToDelete = existingIds.filter((id: string) => !newRuleIds.includes(id));

            for (const id of idsToDelete) {
                await this.api3Service.delete(`partner/${this.partner.slug}/ltl-rules/${id}`);
            }

            for (const rule of rulesToSave) {
                const payload = {
                    hubs: rule.hubs,
                    packageTypes: rule.packageTypes,
                    setOnHold: rule.setOnHold,
                    remark: rule.remark,
                    order: rule.order
                };

                if (rule._id || rule.id) {
                    await this.api3Service.put(
                        `partner/${this.partner.slug}/ltl-rules/${rule._id || rule.id}`,
                        payload
                    );
                } else {
                    await this.api3Service.post(`partner/${this.partner.slug}/ltl-rules`, payload);
                }
            }

            this.toastService.show("LTL rules saved successfully", "success");
            this.form.markAsPristine();
            this.loadLtlSettings();
            this.updated.emit();
        } catch (error) {
            this.toastService.show("Failed to save LTL rules", "error");
        } finally {
            this.saving = false;
            this.updateFormControlsDisabledState();
            this.changeDetectorRef.markForCheck();
        }
    }

    private hasIncompleteRules(): boolean {
        return this.rules.controls.some((group: FormGroup): boolean => {
            const value = group.getRawValue();
            const hubs = value.hubs;
            const packageTypes = value.packageTypes;
            return (Array.isArray(hubs) && !hubs.length) || (Array.isArray(packageTypes) && !packageTypes.length);
        });
    }

    private createRuleGroup(rule?: ILtlRule): FormGroup {
        const isDisabled = this.saving || this.loading.hubs || this.loading.packageTypes;
        return this.formBuilder.group({
            _id: [rule?._id || rule?.id || null],
            id: [rule?._id || rule?.id || null],
            hubs: [{value: rule?.hubs || [], disabled: isDisabled || this.loading.hubs}],
            packageTypes: [{value: rule?.packageTypes || [], disabled: isDisabled || this.loading.packageTypes}],
            setOnHold: [{value: rule?.setOnHold ?? true, disabled: isDisabled}],
            remark: [{value: rule?.remark || "", disabled: isDisabled}],
            order: [rule?.order !== undefined ? rule.order : null]
        });
    }

    private updateFormControlsDisabledState(): void {
        const isDisabled = this.saving || this.loading.hubs || this.loading.packageTypes;
        this.rules.controls.forEach((group: FormGroup) => {
            const hubsControl = group.get("hubs");
            const packageTypesControl = group.get("packageTypes");
            const setOnHoldControl = group.get("setOnHold");
            const remarkControl = group.get("remark");

            if (hubsControl) {
                if (isDisabled || this.loading.hubs) {
                    hubsControl.disable();
                } else {
                    hubsControl.enable();
                }
            }

            if (packageTypesControl) {
                if (isDisabled || this.loading.packageTypes) {
                    packageTypesControl.disable();
                } else {
                    packageTypesControl.enable();
                }
            }

            if (setOnHoldControl) {
                if (isDisabled) {
                    setOnHoldControl.disable();
                } else {
                    setOnHoldControl.enable();
                }
            }

            if (remarkControl) {
                if (isDisabled) {
                    remarkControl.disable();
                } else {
                    remarkControl.enable();
                }
            }
        });
    }

    private async loadLtlSettings(): Promise<void> {
        if (!this.partner) {
            return;
        }

        try {
            const response = await this.api3Service.get(`partner/${this.partner.slug}/ltl-rules`);
            const formArray = this.rules;

            Object.keys(this.hubSearchControls).forEach(key => {
                const index = parseInt(key, 10);
                if (this.hubSearchControls[index]) {
                    this.hubSearchControls[index].setValue("");
                }
            });
            this.hubSearchControls = {};
            this.filteredHubs = {};

            while (formArray.length) {
                formArray.removeAt(0);
            }

            if (response && response.data && Array.isArray(response.data)) {
                const rules = this.normalizeRules(response.data);
                if (!rules.length) {
                    this.addRule({
                        hubs: [],
                        packageTypes: [],
                        setOnHold: true,
                        remark: this.defaultRemark
                    });
                } else {
                    rules.forEach((rule: ILtlRule, index: number): void => {
                        formArray.push(this.createRuleGroup(rule));
                        this.initHubSearchControl(index);
                    });
                }
            } else {
                this.addRule({
                    hubs: [],
                    packageTypes: [],
                    setOnHold: true,
                    remark: this.defaultRemark
                });
            }

            this.form.markAsPristine();
            this.updateFormControlsDisabledState();
            this.changeDetectorRef.markForCheck();
        } catch (error) {
            const formArray = this.rules;

            Object.keys(this.hubSearchControls).forEach(key => {
                const index = parseInt(key, 10);
                if (this.hubSearchControls[index]) {
                    this.hubSearchControls[index].setValue("");
                }
            });
            this.hubSearchControls = {};
            this.filteredHubs = {};

            while (formArray.length) {
                formArray.removeAt(0);
            }
            this.addRule({
                hubs: [],
                packageTypes: [],
                setOnHold: true,
                remark: this.defaultRemark
            });
            this.form.markAsPristine();
            this.updateFormControlsDisabledState();
            this.changeDetectorRef.markForCheck();
        }
    }

    private normalizeRules(source: any[]): ILtlRule[] {
        if (!Array.isArray(source)) {
            return [];
        }

        return source.map((rule: any): ILtlRule => ({
            _id: rule._id || rule.id,
            id: rule._id || rule.id,
            hubs: Array.isArray(rule?.hubs) ? [...rule.hubs] : [],
            packageTypes: Array.isArray(rule?.package_types) ? [...rule.package_types] : [],
            setOnHold: rule?.set_on_hold !== false,
            remark: rule?.remark || this.defaultRemark,
            order: rule?.order !== undefined ? rule.order : 0
        }));
    }

    private async loadResources(): Promise<void> {
        if (!this.partner) {
            return;
        }
        await Promise.all([
            this.fetchHubs(),
            this.fetchPackageTypes()
        ]);
    }

    private async fetchHubs(): Promise<void> {
        if (!this.partner?.slug) {
            return;
        }
        this.loading.hubs = true;
        this.updateFormControlsDisabledState();
        this.changeDetectorRef.markForCheck();
        try {
            const {data}: Api.IResponse = await this.api3Service.get(`partner/${this.partner.slug}/hubs`, {
                relations: [
                    "Warehouse:slug,name"
                ]
            });
            this.hubs = Array.isArray(data)
                ? data.filter((hub: Warehouse.IHub): boolean => !!hub.is_active)
                : [];

            for (let i = 0; i < this.rules.length; i++) {
                if (!this.filteredHubs[i]) {
                    this.filteredHubs[i] = [...this.hubs];
                } else {
                    const searchControl = this.hubSearchControls[i];
                    if (searchControl) {
                        this.filterHubs(i, searchControl.value || "");
                    } else {
                        this.filteredHubs[i] = [...this.hubs];
                    }
                }
            }
        } finally {
            this.loading.hubs = false;
            this.updateFormControlsDisabledState();
            this.changeDetectorRef.markForCheck();
        }
    }

    private async fetchPackageTypes(): Promise<void> {
        if (!this.partner?.slug) {
            return;
        }
        this.loading.packageTypes = true;
        this.updateFormControlsDisabledState();
        this.changeDetectorRef.markForCheck();
        try {
            const {data}: Api.IResponse = await this.api3Service.get(
                `partner/${this.partner.slug}/warehouse-package-types`
            );
            this.packageTypes = Array.isArray(data)
                ? data.filter((type: Warehouse.IPackageType): boolean => type.is_active !== false)
                : [];
        } finally {
            this.loading.packageTypes = false;
            this.updateFormControlsDisabledState();
            this.changeDetectorRef.markForCheck();
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}

